import { API_BASE_URL } from './api';

interface AuthConfig {
  domain: string;
  clientId: string;
  region: string;
  userPoolId: string;
}

let cachedConfig: AuthConfig | null = null;

export async function getAuthConfig(): Promise<AuthConfig> {
  if (cachedConfig) return cachedConfig;

  const response = await fetch(`${API_BASE_URL}/api/auth/config`);
  if (!response.ok) throw new Error('Failed to load auth config');

  cachedConfig = await response.json();
  return cachedConfig!;
}

export function getCallbackUrl(): string {
  return `${window.location.origin}/auth/callback`;
}

export async function getLoginUrl(): Promise<string> {
  const config = await getAuthConfig();
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: getCallbackUrl(),
    scope: 'openid email profile',
    identity_provider: 'JouleIdentityCenter'
  });
  return `${config.domain}/oauth2/authorize?${params.toString()}`;
}

export async function getLogoutUrl(): Promise<string> {
  const config = await getAuthConfig();
  const params = new URLSearchParams({
    client_id: config.clientId,
    logout_uri: window.location.origin
  });
  return `${config.domain}/logout?${params.toString()}`;
}

export async function exchangeCodeForTokens(code: string): Promise<{
  id_token: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
}> {
  const response = await fetch(`${API_BASE_URL}/api/auth/callback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      code,
      redirect_uri: getCallbackUrl()
    })
  });

  if (!response.ok) {
    throw new Error('Token exchange failed');
  }

  return response.json();
}

export async function refreshTokens(): Promise<{
  id_token: string;
  access_token: string;
  expires_in: number;
} | null> {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return null;

  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken })
    });

    if (!response.ok) return null;

    const tokens = await response.json();
    localStorage.setItem('authToken', tokens.id_token);
    localStorage.setItem('accessToken', tokens.access_token);
    return tokens;
  } catch {
    return null;
  }
}
